/**
 * SidebarBadge — círculo con count + color por severidad, al lado del
 * ítem del sidebar. Ciclo 17.
 *
 * Regla de producto:
 *  - count 0 → no renderiza nada (null).
 *  - severity null/undefined con count>0 → render con color "info".
 *  - count > 99 → muestra "99+".
 *  - El color viene de severity:
 *      info     → azul (bg-blue-500)
 *      warning  → amarillo (bg-amber-500)
 *      breached → rojo (bg-red-600)
 */

import type { BadgeSeverity } from '@/types/sidebarCounters';

interface SidebarBadgeProps {
  count: number;
  severity?: BadgeSeverity | null;
  /** Visual-only: ocultar cuando el sidebar está colapsado con solo íconos. */
  className?: string;
}

const SEVERITY_CLASSES: Record<BadgeSeverity, string> = {
  info: 'bg-blue-500 text-white',
  warning: 'bg-amber-500 text-white',
  breached: 'bg-red-600 text-white',
};

export function SidebarBadge({
  count,
  severity,
  className,
}: SidebarBadgeProps) {
  if (!count || count <= 0) return null;

  const bucket: BadgeSeverity = severity ?? 'info';
  const color = SEVERITY_CLASSES[bucket];
  const display = count > 99 ? '99+' : String(count);

  return (
    <span
      data-testid="sidebar-badge"
      data-severity={bucket}
      className={[
        'inline-flex items-center justify-center',
        'min-w-[20px] h-5 px-1.5',
        'rounded-full text-[10px] font-bold leading-none',
        color,
        className ?? '',
      ].join(' ')}
      aria-label={`${count} pendientes`}
    >
      {display}
    </span>
  );
}
